import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, Users, Building2, Plus, Edit2, Trash2, 
  Check, X, ChevronDown, ChevronRight, Search,
  UserPlus, Settings, Save, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Permission toggle component
const PermissionToggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`w-10 h-6 rounded-full transition-colors ${
      checked ? 'bg-emerald-500' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${
      checked ? 'translate-x-5' : 'translate-x-1'
    }`} />
  </button>
);

// Role Card Component
const RoleCard = ({ role, onEdit, onDelete, isExpanded, onToggleExpand }) => {
  const getCategoryColor = (category) => {
    switch (category) {
      case 'direction': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'copropriete': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'gerance': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'direction': return 'Direction';
      case 'copropriete': return 'Copropriété';
      case 'gerance': return 'Gérance';
      default: return 'Personnalisé';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div 
        className="p-4 cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            <div>
              <h3 className="font-semibold text-gray-900">{role.name}</h3>
              <p className="text-sm text-gray-500">{role.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(role.category)}`}>
              {getCategoryLabel(role.category)}
            </span>
            {role.is_system && (
              <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                Système
              </span>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <div className="flex justify-end gap-2 mb-4">
            <button
              onClick={() => onEdit(role)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
            >
              <Edit2 size={16} />
              Modifier les permissions
            </button>
            {!role.is_system && (
              <button
                onClick={() => onDelete(role)}
                className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 text-sm"
              >
                <Trash2 size={16} />
                Supprimer
              </button>
            )}
          </div>

          {/* Permissions summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {role.permissions && Object.entries(role.permissions).map(([module, perms]) => (
              <div key={module} className="flex items-center gap-2 text-sm">
                <span className="capitalize text-gray-700">{module.replace('_', ' ')}</span>
                <div className="flex gap-1">
                  {perms.read && <span className="w-2 h-2 rounded-full bg-blue-400" title="Lecture" />}
                  {perms.write && <span className="w-2 h-2 rounded-full bg-green-400" title="Écriture" />}
                  {perms.delete && <span className="w-2 h-2 rounded-full bg-red-400" title="Suppression" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Permission Editor Modal
const PermissionEditorModal = ({ role, modules, onSave, onClose }) => {
  const [permissions, setPermissions] = useState(role?.permissions || {});
  const [saving, setSaving] = useState(false);

  const handlePermissionChange = (module, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: value
      }
    }));
  };

  const handleSetAll = (action, value) => {
    const updated = {};
    modules.forEach(m => {
      updated[m.code] = {
        ...permissions[m.code],
        [action]: value
      };
    });
    setPermissions(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(permissions);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Modifier les permissions</h2>
              <p className="text-gray-500">{role.name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* Quick actions */}
          <div className="flex gap-4 mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Tout activer:</span>
              <button 
                onClick={() => handleSetAll('read', true)}
                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
              >
                Lecture
              </button>
              <button 
                onClick={() => handleSetAll('write', true)}
                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
              >
                Écriture
              </button>
              <button 
                onClick={() => handleSetAll('delete', true)}
                className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                Suppression
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Tout désactiver:</span>
              <button 
                onClick={() => handleSetAll('read', false)}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Lecture
              </button>
              <button 
                onClick={() => handleSetAll('write', false)}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Écriture
              </button>
              <button 
                onClick={() => handleSetAll('delete', false)}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Suppression
              </button>
            </div>
          </div>

          {/* Permissions table */}
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500 border-b">
                <th className="pb-3 font-medium">Module</th>
                <th className="pb-3 font-medium text-center">Lecture</th>
                <th className="pb-3 font-medium text-center">Écriture</th>
                <th className="pb-3 font-medium text-center">Suppression</th>
              </tr>
            </thead>
            <tbody>
              {modules.map(module => (
                <tr key={module.code} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4">
                    <div>
                      <p className="font-medium text-gray-900">{module.name}</p>
                      <p className="text-sm text-gray-500">{module.description}</p>
                    </div>
                  </td>
                  <td className="py-4 text-center">
                    <div className="flex justify-center">
                      <PermissionToggle
                        checked={permissions[module.code]?.read || false}
                        onChange={(v) => handlePermissionChange(module.code, 'read', v)}
                      />
                    </div>
                  </td>
                  <td className="py-4 text-center">
                    <div className="flex justify-center">
                      <PermissionToggle
                        checked={permissions[module.code]?.write || false}
                        onChange={(v) => handlePermissionChange(module.code, 'write', v)}
                      />
                    </div>
                  </td>
                  <td className="py-4 text-center">
                    <div className="flex justify-center">
                      <PermissionToggle
                        checked={permissions[module.code]?.delete || false}
                        onChange={(v) => handlePermissionChange(module.code, 'delete', v)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// User Role Assignment Modal
const UserAssignmentModal = ({ users, roles, cabinets, onAssign, onClose }) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCabinet, setSelectedCabinet] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [assigning, setAssigning] = useState(false);

  const filteredUsers = users.filter(u => 
    u.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAssign = async () => {
    if (!selectedUser || !selectedRole || !selectedCabinet) {
      toast.error('Veuillez sélectionner un utilisateur, un rôle et un cabinet');
      return;
    }
    setAssigning(true);
    await onAssign(selectedUser, selectedRole, selectedCabinet);
    setAssigning(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Assigner un rôle</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* User search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Utilisateur</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            {searchTerm && (
              <div className="mt-2 max-h-40 overflow-auto border border-gray-200 rounded-lg">
                {filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => {
                      setSelectedUser(user.id);
                      setSearchTerm(`${user.prenom} ${user.nom}`);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${
                      selectedUser === user.id ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <p className="font-medium">{user.prenom} {user.nom}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Role selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Sélectionner un rôle</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>

          {/* Cabinet selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cabinet</label>
            <select
              value={selectedCabinet}
              onChange={(e) => setSelectedCabinet(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Sélectionner un cabinet</option>
              {cabinets.map(cabinet => (
                <option key={cabinet.id} value={cabinet.id}>{cabinet.nom}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            onClick={handleAssign}
            disabled={assigning || !selectedUser || !selectedRole || !selectedCabinet}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            {assigning ? <RefreshCw size={16} className="animate-spin" /> : <UserPlus size={16} />}
            Assigner
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Role Modal
const CreateRoleModal = ({ modules, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'custom',
    hierarchy_level: 3
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Le code et le nom sont requis');
      return;
    }
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Créer un nouveau rôle</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Code (identifiant unique)</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value.toLowerCase().replace(/\s/g, '_')})}
              placeholder="ex: assistant_comptable"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom du rôle</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="ex: Assistant Comptable"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Description du rôle..."
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="direction">Direction</option>
              <option value="copropriete">Copropriété</option>
              <option value="gerance">Gérance</option>
              <option value="custom">Personnalisé</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Niveau hiérarchique</label>
            <select
              value={formData.hierarchy_level}
              onChange={(e) => setFormData({...formData, hierarchy_level: parseInt(e.target.value)})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value={1}>Niveau 1 (Direction)</option>
              <option value={2}>Niveau 2 (Responsable)</option>
              <option value={3}>Niveau 3 (Opérationnel)</option>
            </select>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !formData.code || !formData.name}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
            Créer le rôle
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function RolesManagement() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [expandedRole, setExpandedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [activeTab, setActiveTab] = useState('roles');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch roles
      const rolesRes = await fetch(`${API_URL}/api/roles`, { headers });
      if (rolesRes.ok) {
        setRoles(await rolesRes.json());
      }

      // Fetch modules
      const modulesRes = await fetch(`${API_URL}/api/roles/modules/list`, { headers });
      if (modulesRes.ok) {
        setModules(await modulesRes.json());
      }

      // Fetch users for assignment
      const usersRes = await fetch(`${API_URL}/api/users`, { headers });
      if (usersRes.ok) {
        setUsers(await usersRes.json());
      }

      // Fetch cabinets
      const cabinetsRes = await fetch(`${API_URL}/api/cabinets`, { headers });
      if (cabinetsRes.ok) {
        setCabinets(await cabinetsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInitRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/roles/init`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const handleSavePermissions = async (permissions) => {
    try {
      const response = await fetch(`${API_URL}/api/roles/${editingRole.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions })
      });
      if (response.ok) {
        toast.success('Permissions mises à jour');
        setEditingRole(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCreateRole = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success('Rôle créé avec succès');
        setShowCreateModal(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Supprimer le rôle "${role.name}" ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/roles/${role.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Rôle supprimé');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAssignRole = async (userId, roleId, cabinetId) => {
    try {
      const response = await fetch(`${API_URL}/api/roles/users/${userId}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role_id: roleId, cabinet_id: cabinetId })
      });
      if (response.ok) {
        toast.success('Rôle assigné avec succès');
        setShowAssignModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  // Group roles by category
  const groupedRoles = roles.reduce((acc, role) => {
    const category = role.category || 'custom';
    if (!acc[category]) acc[category] = [];
    acc[category].push(role);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={32} className="animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="roles-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="text-emerald-500" />
            Gestion des Rôles
          </h1>
          <p className="text-gray-500 mt-1">Créez et gérez les rôles et permissions de votre cabinet</p>
        </div>
        <div className="flex gap-3">
          {roles.length === 0 && (
            <button
              onClick={handleInitRoles}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <RefreshCw size={16} />
              Initialiser les rôles
            </button>
          )}
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <UserPlus size={16} />
            Assigner un rôle
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
          >
            <Plus size={16} />
            Nouveau rôle
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('roles')}
          className={`pb-3 px-2 font-medium transition-colors ${
            activeTab === 'roles' 
              ? 'text-emerald-600 border-b-2 border-emerald-500' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Shield size={18} />
            Rôles ({roles.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('assignments')}
          className={`pb-3 px-2 font-medium transition-colors ${
            activeTab === 'assignments' 
              ? 'text-emerald-600 border-b-2 border-emerald-500' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users size={18} />
            Assignations
          </div>
        </button>
      </div>

      {activeTab === 'roles' && (
        <>
          {/* Legend */}
          <div className="flex items-center gap-6 mb-6 text-sm text-gray-600">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-400" /> Lecture
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-400" /> Écriture
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-400" /> Suppression
            </span>
          </div>

          {/* Roles by category */}
          {Object.entries(groupedRoles).map(([category, categoryRoles]) => (
            <div key={category} className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                {category === 'direction' && <Building2 size={20} className="text-purple-500" />}
                {category === 'copropriete' && <Building2 size={20} className="text-blue-500" />}
                {category === 'gerance' && <Building2 size={20} className="text-green-500" />}
                {category === 'custom' && <Settings size={20} className="text-gray-500" />}
                {category === 'direction' ? 'Direction' : 
                 category === 'copropriete' ? 'Pôle Copropriété' : 
                 category === 'gerance' ? 'Pôle Gérance' : 'Rôles personnalisés'}
              </h2>
              <div className="space-y-3">
                {categoryRoles.map(role => (
                  <RoleCard
                    key={role.id}
                    role={role}
                    isExpanded={expandedRole === role.id}
                    onToggleExpand={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
                    onEdit={setEditingRole}
                    onDelete={handleDeleteRole}
                  />
                ))}
              </div>
            </div>
          ))}

          {roles.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Shield size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">Aucun rôle configuré</h3>
              <p className="text-gray-500 mb-4">Initialisez les rôles par défaut pour commencer</p>
              <button
                onClick={handleInitRoles}
                className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
              >
                Initialiser les rôles
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'assignments' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <p className="text-gray-500 text-center py-8">
            Utilisez le bouton "Assigner un rôle" pour attribuer des rôles aux utilisateurs
          </p>
        </div>
      )}

      {/* Modals */}
      {editingRole && (
        <PermissionEditorModal
          role={editingRole}
          modules={modules}
          onSave={handleSavePermissions}
          onClose={() => setEditingRole(null)}
        />
      )}

      {showCreateModal && (
        <CreateRoleModal
          modules={modules}
          onSave={handleCreateRole}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showAssignModal && (
        <UserAssignmentModal
          users={users}
          roles={roles}
          cabinets={cabinets}
          onAssign={handleAssignRole}
          onClose={() => setShowAssignModal(false)}
        />
      )}
    </div>
  );
}
